// Inquiry-funnel SLA + quick-reply templates (Module 34).
//
// Pure-frontend extension of the existing inquiries data model. No new
// columns, no new RPCs — we derive aging from `created_at + status`
// and the templates are dispatched via plain `mailto:` so we never
// have to extend the notifications system for transactional sales mail.

const HOUR = 60 * 60 * 1000;

/**
 * Aging bucket for an inquiry. Only inquiries still in `new` count
 * against an SLA — once an admin has marked it `contacted`, we trust
 * them. `archived` is always "rested".
 *
 * Buckets:
 *   - fresh:   < 8 h since creation (and still `new`)
 *   - aging:   8–24 h
 *   - overdue: > 24 h
 *   - rested:  not `new` (i.e. someone already replied or archived it)
 */
export function inquiryAgeBucket(row, now = Date.now()) {
  if (!row || row.status !== "new") return "rested";
  const created = new Date(row.created_at).getTime();
  if (Number.isNaN(created)) return "rested";
  const hours = (now - created) / HOUR;
  if (hours < 8)   return "fresh";
  if (hours < 24)  return "aging";
  return "overdue";
}

/**
 * Tone tokens for the badge — kept warm, never alarmist. Even
 * `overdue` uses our coral, not red, because this is a funnel
 * nudge, not a system error.
 */
export const AGE_TONE = {
  fresh:   { label: "Fresh",     pill: "bg-primary/10 text-primary-hover border-primary/30" },
  aging:   { label: "Aging",     pill: "bg-[#EFBA57]/10 text-[#8A5A12] border-[#EFBA57]/40" },
  overdue: { label: "Overdue",   pill: "bg-[#C6851E]/10 text-[#C6851E] border-[#C6851E]/30" },
  rested:  null,
};

export function formatAgeShort(iso, now = Date.now()) {
  if (!iso) return "";
  const ms = now - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const hours = Math.floor(ms / HOUR);
  if (hours < 1)  return "just now";
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  return `${days} d`;
}

// ---------------------------------------------------------------------------
// Quick-reply templates
//
// Three short, friendly options that cover ~80% of inquiry replies.
// Each renders to a `mailto:` URL the admin's mail client (Apple Mail,
// Gmail web, Outlook) opens in the default compose window — they review
// and personalise before sending. We intentionally don't auto-send.
// ---------------------------------------------------------------------------

function firstName(parentName) {
  if (!parentName) return "";
  return String(parentName).trim().split(/\s+/)[0];
}

export const QUICK_REPLY_TEMPLATES = [
  {
    key: "thanks-info",
    label: "Thanks · here's our info",
    blurb: "Quick acknowledgement with parent-handbook style info.",
    build({ row, organizationName }) {
      const fn = firstName(row.parent_name) || "there";
      const subject = `Thanks for reaching out about ${organizationName || "our daycare"}`;
      const body = [
        `Hi ${fn},`,
        ``,
        `Thanks so much for getting in touch — we'd love to tell you more about ${organizationName || "our daycare"}.`,
        ``,
        `A few quick details:`,
        `• Hours: 7:00 AM – 6:00 PM, Monday–Friday`,
        `• Ratios: small, age-appropriate groupings`,
        `• Daily updates: photos, meals, naps, and notes via our parent app`,
        ``,
        `If you'd like, I can put together a tour. What does your week look like?`,
        ``,
        `Warmly,`,
      ].join("\n");
      return { subject, body };
    },
  },
  {
    key: "tour-invite",
    label: "Invite to tour",
    blurb: "Suggest two specific time slots.",
    build({ row, organizationName }) {
      const fn = firstName(row.parent_name) || "there";
      const subject = `Tour at ${organizationName || "our daycare"} — a few times that work?`;
      const body = [
        `Hi ${fn},`,
        ``,
        `So glad you're considering us. The best way to get a feel for the place is a short walk-through during the day.`,
        ``,
        `Two times that usually work well for prospective families:`,
        `• Tuesday at 10:00 AM`,
        `• Thursday at 2:30 PM`,
        ``,
        `Either of those okay, or shall I suggest a couple more? I'll have the lead teacher walk you through the classroom and answer anything specific to ${row.child_age || "your little one"}.`,
        ``,
        `Looking forward to meeting you.`,
        ``,
      ].join("\n");
      return { subject, body };
    },
  },
  {
    key: "waitlist",
    label: "Currently on waitlist",
    blurb: "Kind, transparent waitlist note.",
    build({ row, organizationName }) {
      const fn = firstName(row.parent_name) || "there";
      const subject = `Waitlist update for ${organizationName || "our daycare"}`;
      const body = [
        `Hi ${fn},`,
        ``,
        `Thank you for the kind note — it really does mean a lot.`,
        ``,
        `We're at capacity in the age group you asked about right now, but we keep a short, actively-managed waitlist and reach out the moment a spot opens. Most movement happens around new term breaks.`,
        ``,
        `Would you like me to add you to the list? If so, just reply with a good phone number and we'll keep you posted.`,
        ``,
        `With warm regards,`,
      ].join("\n");
      return { subject, body };
    },
  },
];

/**
 * Build the mailto: URL for a template against a row. Email clients
 * cap URL length around 2 kB; our templates stay well under that.
 */
export function buildMailtoUrl(template, { row, organizationName }) {
  const { subject, body } = template.build({ row, organizationName });
  const params = new URLSearchParams();
  params.set("subject", subject);
  params.set("body", body);
  return `mailto:${encodeURIComponent(row.parent_email)}?${params.toString()}`;
}
